import type {
  AddRouteStopsInput,
  CreateRouteInput,
  DuplicateRouteInput,
  OptimizationResult,
  Paginated,
  Route,
  RouteDetail,
  UpdateRouteInput,
  UpdateRouteStopInput,
} from '@fdt/shared';
import { api } from '../api/client';
import type { ListParams } from '../api/resource';

/**
 * Routes have more verbs than a plain CRUD resource — reordering, optimising,
 * duplicating — so they get their own client rather than going through
 * `resource()`.
 *
 * Every mutation returns the whole route, so the caller never has to stitch a
 * partial response into its cache.
 */
export const routesApi = {
  async list(params: ListParams): Promise<Paginated<Route>> {
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
    );
    const { data } = await api.get<Paginated<Route>>('/routes', { params: query });
    return data;
  },

  async get(id: string): Promise<RouteDetail> {
    const { data } = await api.get<RouteDetail>(`/routes/${id}`);
    return data;
  },

  async create(body: CreateRouteInput): Promise<RouteDetail> {
    const { data } = await api.post<RouteDetail>('/routes', body);
    return data;
  },

  async update(id: string, body: UpdateRouteInput): Promise<RouteDetail> {
    const { data } = await api.patch<RouteDetail>(`/routes/${id}`, body);
    return data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/routes/${id}`);
  },

  async duplicate(id: string, body: DuplicateRouteInput): Promise<RouteDetail> {
    const { data } = await api.post<RouteDetail>(`/routes/${id}/duplicate`, body);
    return data;
  },

  async addStops(id: string, body: AddRouteStopsInput): Promise<RouteDetail> {
    const { data } = await api.post<RouteDetail>(`/routes/${id}/stops`, body);
    return data;
  },

  async reorder(id: string, stopIds: string[]): Promise<RouteDetail> {
    const { data } = await api.patch<RouteDetail>(`/routes/${id}/stops/reorder`, { stopIds });
    return data;
  },

  async updateStop(id: string, stopId: string, body: UpdateRouteStopInput): Promise<RouteDetail> {
    const { data } = await api.patch<RouteDetail>(`/routes/${id}/stops/${stopId}`, body);
    return data;
  },

  async removeStop(id: string, stopId: string): Promise<RouteDetail> {
    const { data } = await api.delete<RouteDetail>(`/routes/${id}/stops/${stopId}`);
    return data;
  },

  async optimize(id: string): Promise<OptimizationResult> {
    const { data } = await api.post<OptimizationResult>(`/routes/${id}/optimize`);
    return data;
  },

  async computeGeometry(id: string): Promise<RouteDetail> {
    const { data } = await api.post<RouteDetail>(`/routes/${id}/geometry`);
    return data;
  },
};
